import { useEffect, useState } from "react";
import { UniversalPlatformSearch, type PlatformId } from "@/components/UniversalPlatformSearch";
import { LanguageCurrencySelector } from "@/components/LanguageCurrencySelector";
import { SlimComplianceFooter } from "@/components/SlimComplianceFooter";

/**
 * Ultra-clean single-page ads landing.
 * Pick a platform → one input → one CTA. No headlines, no marketing copy.
 * Designed for paid traffic (Google / Meta / TikTok ads) — maximum focus.
 */

type Tab = { id: PlatformId; label: string; prompt: string };

const TABS: Tab[] = [
  { id: "tiktok",    label: "TikTok",    prompt: "Put your TikTok ID or URL" },
  { id: "instagram", label: "Instagram", prompt: "Put your Instagram ID or URL" },
  { id: "youtube",   label: "YouTube",   prompt: "Put your YouTube channel or video URL" },
  { id: "spotify",   label: "Spotify",   prompt: "Put your Spotify artist name" },
  { id: "twitch",    label: "Twitch",    prompt: "Put your Twitch channel" },
  { id: "twitter",   label: "X",         prompt: "Put your X profile or post URL" },
  { id: "facebook",  label: "Facebook",  prompt: "Put your Facebook page or post URL" },
];

const AdsLandingPage = () => {
  const [active, setActive] = useState<PlatformId>("tiktok");
  const tab = TABS.find((t) => t.id === active)!;

  useEffect(() => {
    document.title = "Grow your social — TokDadddy";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };
    setMeta("description", "Grow your TikTok, Instagram, YouTube, Spotify and more. Enter your handle to start.");
    setMeta("robots", "noindex,follow");

    if (typeof window === "undefined") return;
    const w = window as any;
    w.dataLayer = w.dataLayer || [];

    // Queue the events on dataLayer. GTM will replay them once it loads
    // (on first user interaction or 8s fallback in index.html). Do NOT
    // force-load GTM here — it adds ~766 KB to the LCP critical path and
    // tanks the Google Ads quality score for this paid landing page.
    w.dataLayer.push({
      event: "ads_landing_view",
      page_path: "/go",
      page_title: "Grow your social — TokDadddy",
      traffic_source: "paid",
    });
    w.dataLayer.push({
      event: "page_view",
      page_path: "/go",
      page_title: "Grow your social — TokDadddy",
    });
  }, []);


  return (
    <>
      <main className="min-h-screen bg-background flex flex-col">
        {/* Tiny brand mark + language switcher */}
        <div className="px-4 pt-6 sm:pt-8 flex items-center justify-between max-w-2xl mx-auto w-full">
          <p
            className="text-sm font-bold tracking-tight text-foreground"
            style={{ fontFamily: "var(--font-display)" }}
          >
            TokDadddy
          </p>
          <LanguageCurrencySelector />
        </div>

        <section className="flex-1 flex flex-col justify-center px-4 py-10 sm:py-16">
          <div className="mx-auto w-full max-w-2xl">
            {/* Platform pill tabs */}
            <div
              role="tablist"
              aria-label="Choose platform"
              className="flex flex-wrap justify-center gap-2 mb-8"
            >
              {TABS.map((t) => {
                const isActive = t.id === active;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(t.id)}
                    className={[
                      "px-4 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all",
                      isActive
                        ? "text-primary-foreground"
                        : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80",
                    ].join(" ")}
                    style={
                      isActive
                        ? { background: "var(--hero-gradient)", boxShadow: "var(--glow-button)" }
                        : undefined
                    }
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* Single prompt — only text on the page */}
            <h1
              className="text-center text-2xl sm:text-3xl font-bold text-foreground tracking-tight"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {tab.prompt}
            </h1>

            {/* Search — re-uses existing platform search logic with all dropdowns/CTAs */}
            <div className="mt-6">
              <UniversalPlatformSearch platform={active} ctaLabel="Start" />
            </div>
          </div>
        </section>

        <SlimComplianceFooter />
      </main>
    </>
  );
};

export default AdsLandingPage;
